import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card,
  CardBody,
  Col,
  Row,
  Input,
  Label,
  Progress,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from '../../../components/Common/PageHint';
import { downscaleImage, resizedFileName, MAX_EDGE } from "../../../helpers/imageResize";
import DeleteModal from "../../../components/Common/DeleteModal";

const ODATA = "https://buharaapi.stechomeyazilim.info";
const UPLOAD_URL = "https://buharaadmin.stechomeyazilim.info:8099/uploadfile";
const IMG_BASE = "https://buharasubdomain.stechomeyazilim.info/";

const uploadOne = async (blob, originalName) => {
  const body = new FormData();
  body.append("dataFile", blob, resizedFileName(blob, originalName));
  const res = await axios.post(UPLOAD_URL, body);
  return res?.data?.file?.filename || null;
};

const GalleryPhotos = () => {
  document.title = "Galeri | Buhara - Admin Paneli";

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [saved, setSaved] = useState({ before: 0, after: 0 });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editSort, setEditSort] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [uploadCategory, setUploadCategory] = useState("");

  const fileInputRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ODATA}/Gallery_Photos`);
      const list = res.data?.value || [];
      list.sort(
        (a, b) =>
          (a.Gallery_Photo_Sort ?? 9999) - (b.Gallery_Photo_Sort ?? 9999) ||
          a.ID - b.ID
      );
      setPhotos(list);
    } catch (err) {
      console.error("Galeri çekme hatası:", err);
      toastr.error("Galeri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Adet sınırı yok: seçilen tüm dosyalar sırayla küçültülüp yüklenir.
  const handleFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = ""; // aynı dosyalar tekrar seçilebilsin
    if (!files.length) return;

    setUploading(true);
    setProgress({ done: 0, total: files.length });
    setSaved({ before: 0, after: 0 });

    let before = 0;
    let after = 0;
    let failed = 0;
    const startSort = photos.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const blob = await downscaleImage(file);
        before += file.size;
        after += blob.size;

        const filename = await uploadOne(blob, file.name);
        if (!filename) throw new Error("Dosya adı dönmedi");

        const payload = {
          Gallery_Photo_Url: filename,
          Gallery_Photo_Sort: startSort + i + 1,
          Gallery_Photo_IsActive: true,
        };
        if (uploadCategory.trim()) {
          payload.Gallery_Photo_Title = uploadCategory.trim();
        }
        try {
          if (uploadCategory.trim()) payload.Gallery_Photo_Category = uploadCategory.trim();
          await axios.post(`${ODATA}/Gallery_Photos`, payload);
        } catch (postErr) {
          delete payload.Gallery_Photo_Category;
          await axios.post(`${ODATA}/Gallery_Photos`, payload);
        }
      } catch (err) {
        failed++;
        console.error("Yükleme hatası:", file.name, err);
      }
      setProgress({ done: i + 1, total: files.length });
      setSaved({ before, after });
    }

    setUploading(false);
    await fetchPhotos();

    if (failed === 0) {
      toastr.success(`${files.length} fotoğraf yüklendi.`);
    } else {
      toastr.warning(`${files.length - failed} fotoğraf yüklendi, ${failed} tanesi başarısız.`);
    }
  };

  const openEdit = (photo) => {
    setEditTarget(photo);
    const raw = photo.Gallery_Photo_Title || "";
    const parts = raw.split(/\s*\|\|\s*/);
    setEditCategory(photo.Gallery_Photo_Category || parts[0] || "");
    setEditTitle(parts[1] || (photo.Gallery_Photo_Category ? raw : "") || "");
    setEditSort(photo.Gallery_Photo_Sort ?? "");
    setEditActive(photo.Gallery_Photo_IsActive !== false);
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    try {
      const cat = editCategory.trim();
      const cap = editTitle.trim();
      const title = cat && cap && cat !== cap ? `${cat}||${cap}` : (cat || cap || null);
      const patch = {
        Gallery_Photo_Title: title,
        Gallery_Photo_Sort: editSort === "" ? null : parseInt(editSort, 10),
        Gallery_Photo_IsActive: editActive,
      };
      try {
        if (cat) patch.Gallery_Photo_Category = cat;
        await axios.patch(`${ODATA}/Gallery_Photos(${editTarget.ID})`, patch);
      } catch (patchErr) {
        delete patch.Gallery_Photo_Category;
        await axios.patch(`${ODATA}/Gallery_Photos(${editTarget.ID})`, patch);
      }
      setEditTarget(null);
      await fetchPhotos();
      toastr.success("Fotoğraf güncellendi.");
    } catch (err) {
      console.error(err);
      toastr.error("Güncelleme sırasında hata oluştu.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${ODATA}/Gallery_Photos(${deleteTarget.ID})`);
      setDeleteTarget(null);
      await fetchPhotos();
      toastr.success("Fotoğraf silindi.");
    } catch (err) {
      console.error(err);
      toastr.error("Silme sırasında hata oluştu.");
    }
  };

  const toggleActive = async (photo) => {
    try {
      await axios.patch(`${ODATA}/Gallery_Photos(${photo.ID})`, {
        Gallery_Photo_IsActive: !(photo.Gallery_Photo_IsActive !== false),
      });
      await fetchPhotos();
    } catch (err) {
      console.error(err);
      toastr.error("İşlem sırasında hata oluştu.");
    }
  };

  const mb = (bytes) => (bytes / 1048576).toFixed(1) + " MB";

  return (
    <React.Fragment>
      <DeleteModal
        show={!!deleteTarget}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteTarget(null)}
      />

      {/* Yükleme ilerlemesi */}
      <Modal isOpen={uploading} backdrop="static" centered>
        <ModalBody>
          <p className="text-center fw-bold mb-3">Fotoğraflar yükleniyor…</p>
          <Progress
            value={progress.total ? (progress.done / progress.total) * 100 : 0}
            color="success"
            animated
          />
          <p className="text-center mt-2 mb-0">
            {progress.done} / {progress.total}
          </p>
          {saved.before > 0 && (
            <p className="text-center text-muted small mb-0">
              Küçültme: {mb(saved.before)} → {mb(saved.after)}
            </p>
          )}
        </ModalBody>
      </Modal>

      {/* Düzenleme */}
      <Modal isOpen={!!editTarget} toggle={() => setEditTarget(null)} centered>
        <ModalHeader toggle={() => setEditTarget(null)} tag="h4">
          Fotoğrafı Düzenle
        </ModalHeader>
        <ModalBody>
          {editTarget && (
            <div className="text-center mb-3">
              <img
                src={IMG_BASE + editTarget.Gallery_Photo_Url}
                alt=""
                style={{ maxHeight: 180, maxWidth: "100%", borderRadius: 10 }}
              />
            </div>
          )}
          <div className="mb-3">
            <Label className="form-label">Kategori</Label>
            <Input
              type="text"
              list="gallery-category-list"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              placeholder="Örn: Fuar 2026"
            />
          </div>
          <div className="mb-3">
            <Label className="form-label">Açıklama (isteğe bağlı)</Label>
            <Input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Örn: Stant görünümü"
            />
          </div>
          <div className="mb-3">
            <Label className="form-label">Sıra</Label>
            <Input
              type="number"
              value={editSort}
              onChange={(e) => setEditSort(e.target.value)}
              placeholder="Küçük sayı önce gösterilir"
            />
          </div>
          <div className="mb-3">
            <Label className="form-label">Sitede Görünsün Mü?</Label>
            <div className="square-switch">
              <input
                type="checkbox"
                id="gallery-active-switch"
                className="switch"
                checked={editActive}
                onChange={() => setEditActive((v) => !v)}
              />
              <label
                htmlFor="gallery-active-switch"
                data-on-label="Evet"
                data-off-label="Hayır"
              />
            </div>
          </div>
          <div className="text-end pt-2 border-top">
            <button className="btn btn-light me-2" onClick={() => setEditTarget(null)}>
              İptal
            </button>
            <button className="btn btn-success" onClick={saveEdit}>
              Kaydet
            </button>
          </div>
        </ModalBody>
      </Modal>

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Galeri" />
          <PageHint
            what="Site galerisindeki fotoğraflar bu ekrandan yönetilir."
            where="Galeri sayfasındaki fotoğraf ızgarası. Fotoğraf sayısında sınır yoktur; sıralama alanı küçükten büyüğe dizer."
            links={[{ label: 'Galeri sayfası', path: '/galeri' }]}
          />

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                      <h5 className="card-title mb-1">Site Galerisi</h5>
                      <p className="text-muted mb-0 small">
                        Adet sınırı yok — birden fazla fotoğrafı aynı anda seçebilirsiniz.
                        Yüklenmeden önce her fotoğraf otomatik olarak en fazla {MAX_EDGE}px
                        boyutuna küçültülür.
                      </p>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Input
                        type="text"
                        list="gallery-category-list"
                        value={uploadCategory}
                        onChange={(e) => setUploadCategory(e.target.value)}
                        placeholder="Kategori (ör. Fuar 2026)"
                        style={{ minWidth: 220 }}
                      />
                      <datalist id="gallery-category-list">
                        {[...new Set(photos.map((p) => (p.Gallery_Photo_Category || (p.Gallery_Photo_Title || "").split("||")[0] || "").trim()).filter(Boolean))].map((c) => (
                          <option key={c} value={c} />
                        ))}
                      </datalist>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFiles}
                      />
                      <button
                        className="btn btn-success btn-rounded"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        disabled={uploading}
                      >
                        <i className="mdi mdi-plus me-1" />
                        Fotoğraf Ekle
                      </button>
                    </div>
                  </div>

                  {loading ? (
                    <p className="text-muted text-center py-4 mb-0">Yükleniyor…</p>
                  ) : photos.length === 0 ? (
                    <p className="text-muted text-center py-5 mb-0">
                      Henüz fotoğraf eklenmemiş. &quot;Fotoğraf Ekle&quot; ile başlayın.
                    </p>
                  ) : (
                    <>
                      <p className="text-muted small mb-3">Toplam {photos.length} fotoğraf</p>
                      <Row>
                        {photos.map((photo) => {
                          const active = photo.Gallery_Photo_IsActive !== false;
                          return (
                            <Col xl="2" lg="3" md="4" sm="6" key={photo.ID} className="mb-4">
                              <div className="border rounded overflow-hidden h-100 d-flex flex-column">
                                <div style={{ position: "relative", paddingTop: "75%", background: "#f3f6f4" }}>
                                  <img
                                    src={IMG_BASE + photo.Gallery_Photo_Url}
                                    alt={photo.Gallery_Photo_Title || ""}
                                    loading="lazy"
                                    style={{
                                      position: "absolute",
                                      inset: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      opacity: active ? 1 : 0.4,
                                    }}
                                  />
                                  {!active && (
                                    <span
                                      className="badge bg-secondary"
                                      style={{ position: "absolute", top: 8, left: 8 }}
                                    >
                                      Gizli
                                    </span>
                                  )}
                                </div>
                                <div className="p-2 flex-grow-1 d-flex flex-column">
                                  <div className="small text-truncate mb-2" title={photo.Gallery_Photo_Title || ""}>
                                    {photo.Gallery_Photo_Category || photo.Gallery_Photo_Title || (
                                      <span className="text-muted">Kategori yok</span>
                                    )}
                                  </div>
                                  <div className="mt-auto d-flex gap-1">
                                    <button
                                      className="btn btn-sm btn-outline-secondary flex-grow-1"
                                      onClick={() => openEdit(photo)}
                                      title="Düzenle"
                                    >
                                      <i className="mdi mdi-pencil" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => toggleActive(photo)}
                                      title={active ? "Sitede gizle" : "Sitede göster"}
                                    >
                                      <i className={active ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => setDeleteTarget(photo)}
                                      title="Sil"
                                    >
                                      <i className="mdi mdi-delete" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default GalleryPhotos;
